/**
 * Guacamole Player Service
 * ------------------------
 * Creates and manages a Guacamole.SessionRecording instance for playing
 * .guac session recordings served by the Watch API.
 *
 * The recording file is fetched from:
 *   GET /sessions/{id}/recording
 *
 * Authentication is handled by appending the API key as a query parameter
 * because Guacamole.StaticHTTPTunnel does not support custom headers —
 * the tunnel performs a raw XMLHttpRequest under the hood.
 *
 * Usage:
 *   const player = createGuacPlayer({ mountPoint, src, onReady, ... });
 *   player.togglePlay();
 *   player.seek(30);       // seek to 30 seconds
 *   player.disconnect();
 */

/**
 * @typedef {Object} GuacPlayerOptions
 * @property {HTMLElement} mountPoint    - DOM element where the canvas is mounted
 * @property {string}      src           - Full URL to the .guac recording file
 * @property {function}    [onReady]     - Called when the first frame is rendered
 * @property {function}    [onPlay]      - Called when playback starts
 * @property {function}    [onPause]     - Called when playback pauses
 * @property {function}    [onTime]      - Called with current position in seconds
 * @property {function}    [onDuration]  - Called with total duration in seconds
 * @property {function}    [onResolution]- Called with (width, height)
 * @property {function}    [onError]     - Called with an error message string
 */

/**
 * Creates a Guacamole session recording player.
 *
 * @param {GuacPlayerOptions} options
 * @returns {{ togglePlay: function, seek: function, disconnect: function }}
 */
export function createGuacPlayer({
  mountPoint,
  src,
  apiKey,
  onReady,
  onPlay,
  onPause,
  onTime,
  onDuration,
  onResolution,
  onError,
}) {
  if (!mountPoint) {
    throw new Error("[guacService] mountPoint element is required");
  }

  if (typeof Guacamole === "undefined") {
    onError?.("Guacamole library is not loaded");
    return { togglePlay() {}, seek() {}, disconnect() {} };
  }

  let tunnel      = null;
  let recording   = null;
  let display     = null;
  let destroyed   = false;
  let initialized = false;
  let initializing = true;

  // ---------------------------------------------------------------------------
  // Build authenticated URL
  //
  // StaticHTTPTunnel does not support custom headers, so we append the API
  // key as a query parameter. The Watch API accepts X-API-Key both as a
  // header and as a query param for this reason.
  // ---------------------------------------------------------------------------

  const authenticatedSrc = src.includes("?")
    ? `${src}&token=${apiKey ?? ""}`
    : `${src}?token=${apiKey ?? ""}`;

  // ---------------------------------------------------------------------------
  // Initialize tunnel, recording and display
  // ---------------------------------------------------------------------------

  try {
    tunnel    = new Guacamole.StaticHTTPTunnel(authenticatedSrc);
    recording = new Guacamole.SessionRecording(tunnel);
    display   = recording.getDisplay();
  } catch (err) {
    console.error("[guacService] Initialization failed:", err);
    onError?.("Failed to initialize Guacamole player");
    return { togglePlay() {}, seek() {}, disconnect() {} };
  }

  // ---------------------------------------------------------------------------
  // Mount canvas
  // ---------------------------------------------------------------------------

  const canvas = display.getElement();
  canvas.style.maxWidth  = "100%";
  canvas.style.maxHeight = "100%";
  canvas.style.objectFit = "contain";
  canvas.style.display   = "block";
  canvas.style.margin    = "auto";
  canvas.style.opacity   = "0";
  canvas.style.transition = "opacity 0.2s ease";

  mountPoint.innerHTML = "";
  mountPoint.appendChild(canvas);

  // ---------------------------------------------------------------------------
  // Connect and seek to frame 0 to render the first image
  // ---------------------------------------------------------------------------

  try {
    recording.connect();
  } catch (err) {
    console.error("[guacService] Connection failed:", err);
    onError?.("Failed to connect to recording");
    return { togglePlay() {}, seek() {}, disconnect() {} };
  }

  setTimeout(() => {
    if (destroyed) return;
    _initFirstFrame();
  }, 150);

  function _initFirstFrame() {
    if (destroyed || initialized) return;

    try {
      recording.seek(0, () => {
        if (destroyed) return;

        canvas.style.opacity = "1";
        initialized  = true;
        initializing = false;

        onReady?.();

        const durationMs = recording.getDuration();
        if (durationMs > 0) onDuration?.(durationMs / 1000);
      });
    } catch (err) {
      console.error("[guacService] First-frame seek failed:", err);
      initializing = false;
      onError?.("Failed to render first frame");
    }
  }

  // ---------------------------------------------------------------------------
  // Progress events
  // ---------------------------------------------------------------------------

  const PROGRESS_THROTTLE_MS = 100;
  let lastProgressAt = 0;

  recording.onprogress = (ms) => {
    if (destroyed || initializing) return;

    const now = Date.now();
    if (now - lastProgressAt < PROGRESS_THROTTLE_MS) return;
    lastProgressAt = now;

    onTime?.(ms / 1000);

    const total = recording.getDuration();
    if (total > 0) onDuration?.(total / 1000);
  };

  recording.onseek = (ms) => {
    if (destroyed || initializing) return;
    onTime?.(ms / 1000);
  };

  // Resolution changes
  recording.oninstruction = (opcode, args) => {
    if (destroyed || opcode !== "size") return;
    const w = parseInt(args[0], 10);
    const h = parseInt(args[1], 10);
    if (w > 0 && h > 0) onResolution?.(w, h);
  };

  display.onresize = (w, h) => {
    if (!destroyed) onResolution?.(w, h);
  };

  recording.onerror = (err) => {
    if (destroyed) return;
    initializing = false;
    console.error("[guacService] Recording error:", err);
    onError?.(err.toString());
  };

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  function togglePlay() {
    if (destroyed || !initialized) return;

    if (recording.isPlaying()) {
      recording.pause();
      onPause?.();
    } else {
      recording.play();
      onPlay?.();
    }
  }

  function seek(seconds) {
    if (destroyed || !initialized) return;
    recording.seek(Math.max(0, Math.floor(seconds * 1000)));
  }

  function disconnect() {
    if (destroyed) return;
    destroyed = true;

    try {
      if (recording?.isPlaying()) recording.pause();
      recording?.disconnect();
      tunnel?.disconnect();
    } catch (err) {
      console.warn("[guacService] Disconnect cleanup error:", err);
    }

    if (mountPoint) mountPoint.innerHTML = "";

    tunnel      = null;
    recording   = null;
    display     = null;
    initialized  = false;
    initializing = true;
  }

  return { togglePlay, seek, disconnect };
}